import { useEffect, useRef } from 'react';
import { Channel } from '@anycable/web';
import { useAppContext } from '@contexts/app';
import { useWebsocketContext } from '@contexts/websocket';
import { useRevalidateQuery } from '@services/revalidateQueries';
import { formatResponse } from '@utils/textHelpers';
import { REFRESH_DEBOUNCE_MS, REFRESH_JITTER_MS, REFRESH_MAX_WAIT_MS } from './constants';
import { RefreshMessage } from './types';

export const useRefreshChannel = () => {
  const { isLogged, id, tenant } = useAppContext();
  const { ws } = useWebsocketContext();
  const { revalidate } = useRevalidateQuery();
  const tenantId = 'id' in tenant ? tenant.id : undefined;

  const revalidateRef = useRef(revalidate);
  // eslint-disable-next-line immutable/no-mutation
  revalidateRef.current = revalidate;

  useEffect(() => {
    if (!id || !ws || !tenantId || !isLogged) return;

    const pending = new Set<string>();
    let batchStartedAt: number | null = null;
    let batchJitter = 0;
    let timer: ReturnType<typeof setTimeout> | undefined;

    const flush = () => {
      timer = undefined;
      batchStartedAt = null;
      if (document.hidden || !pending.size) return;

      const types = Array.from(pending);
      pending.clear();
      revalidateRef.current(types).catch((err) => {
        console.error(err);
      });
    };

    const scheduleFlush = () => {
      if (document.hidden) return;

      const now = Date.now();
      if (batchStartedAt === null) {
        batchStartedAt = now;
        batchJitter = Math.random() * REFRESH_JITTER_MS;
      }
      const delay = Math.min(REFRESH_DEBOUNCE_MS, batchStartedAt + REFRESH_MAX_WAIT_MS - now);

      clearTimeout(timer);
      timer = setTimeout(flush, Math.max(0, delay) + batchJitter);
    };

    const onVisibilityChange = () => {
      if (document.hidden || !pending.size) return;

      clearTimeout(timer);
      timer = setTimeout(flush, 0);
    };

    const channel = new Channel({ channel: 'RefreshChannel', tenant: tenantId });
    ws.subscribe(channel);

    channel.on('message', (msg) => {
      const { actions } = formatResponse(msg as RefreshMessage);
      if (!actions?.length) return;

      actions.forEach((action) => pending.add(action));
      scheduleFlush();
    });

    document.addEventListener('visibilitychange', onVisibilityChange);

    return () => {
      clearTimeout(timer);
      document.removeEventListener('visibilitychange', onVisibilityChange);
      channel.disconnect();
    };
  }, [id, ws, tenantId, isLogged]);
};
